import { AutomationExtensions } from './widget-automations.model';
import {
  ValidationType,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetState,
} from './widgets.model';
import {
  Automation,
  AutomationEditorConfig,
  AutomationSourceType,
  CustomModelAction,
  TriggerCondition,
  TypeStep,
} from '../automations/automations-models';
import WidgetComponentHelpers from './widget-component-helpers';
import {
  ActionRealTimeScl,
  TriggerSubType,
  TriggerType,
  TriggerTypeArgument,
  ACTIONS_TYPES,
  ACTIONS_TYPES_REAL_TIME,
  ActionWithRealTimeScl,
} from 'app/automations/automation-helpers-actions';
import { AutomationHelpers } from 'app/automations/automation-helpers';
import { Account } from 'app/back-office/account.model';
import { IntegrationType } from 'app/automations/integrations-models';
import { AccountIdentifierConstants } from 'app/account/account-identifier-constants';
import { LanguageService } from 'app/core/language.service';
import { map, Observable } from 'rxjs';

export default class WidgetAutomationHelpers {
  public static EMAIL_TRIGGER_ID = 'Triggerbee.Extensions.Triggers.TriggerDefaultIfs.IfContactEmail';
  public static WIDGET_SUBMIT_TRIGGER_ID = 'Triggerbee.Extensions.Triggers.TriggerDefaultIfs.IfWidgetForm';
  public static SUPPORTED_APP_EXTENSIONS = [
    AutomationExtensions.Voyado,
    AutomationExtensions.MailChimp,
    AutomationExtensions.Pipedrive,
    AutomationExtensions.RuleMailer,
    AutomationExtensions.ApsisOne,
    AutomationExtensions.Apsis,
    AutomationExtensions.Klaviyo,
    AutomationExtensions.Emarsys,
    AutomationExtensions.Custobar,
    AutomationExtensions.MailerLite,
  ];

  private static readonly customInputTextFieldMergeVarPrefix = '{{session.custom.';

  public static isAppExtensionSupported(extension: AutomationExtensions) {
    return this.SUPPORTED_APP_EXTENSIONS.indexOf(extension) !== -1;
  }

  public static getAutomationActionFieldValueMergevar(inputField: WidgetComponent): string {
    if (inputField.type === WidgetComponentType.ButtonList) {
      return this.getCustomInputTextFieldMergeVar(inputField);
    }

    switch (inputField.subType) {
      case WidgetComponentSubType.InputTextName:
        return '{{session.custom.name}}';
      case WidgetComponentSubType.InputTextFirstName:
        return '{{session.custom.firstname}}';
      case WidgetComponentSubType.InputTextLastName:
        return '{{session.custom.lastname}}';
      case WidgetComponentSubType.InputTextPhone:
        return '{{session.custom.telephone}}';
      case WidgetComponentSubType.InputTextEmail:
        return '{{session.custom.email}}';
      case WidgetComponentSubType.DropdownCountry:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.InputTextArea:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.InputTextRegular:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.DateRegular:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.DropdownRegular:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.ButtonListRadio:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.ButtonListChoices:
        return this.getCustomInputTextFieldMergeVar(inputField);
      case WidgetComponentSubType.ButtonListCheckbox:
        return this.getCustomInputTextFieldMergeVar(inputField);
      default:
        throw Error('Unknown input text field: ' + inputField.subType);
    }
  }

  public static getNameOfField(component: WidgetComponent, languageService) {
    if (!component) {
      return languageService.translateInstantly('generic.unknown');
    }
    if (
      component.subType === WidgetComponentSubType.DateRegular ||
      component.subType === WidgetComponentSubType.InputTextArea ||
      component.subType === WidgetComponentSubType.DropdownRegular ||
      component.subType === WidgetComponentSubType.ButtonListChoices ||
      component.subType === WidgetComponentSubType.ButtonListCheckbox ||
      component.subType === WidgetComponentSubType.InputTextRegular ||
      component.subType === WidgetComponentSubType.ButtonListRadio
    ) {
      return component.name ? component.name : component.subType;
    } else {
      const description = WidgetComponentHelpers.getComponentDescriptionBySubtype(component.subType);
      return languageService.translateInstantly(description.title);
    }
  }

  static getWidgetInputComponentByMergeVarName(
    mergeVar: string,
    inputComponents: Array<WidgetComponent>,
  ): WidgetComponent {
    let subTypeToReturn = undefined as WidgetComponentSubType;
    if (mergeVar === '{{session.custom.email}}') {
      subTypeToReturn = WidgetComponentSubType.InputTextEmail;
    } else if (mergeVar === '{{session.custom.name}}') {
      subTypeToReturn = WidgetComponentSubType.InputTextName;
    } else if (mergeVar === '{{session.custom.firstname}}') {
      subTypeToReturn = WidgetComponentSubType.InputTextFirstName;
    } else if (mergeVar === '{{session.custom.lastname}}') {
      subTypeToReturn = WidgetComponentSubType.InputTextLastName;
    } else if (mergeVar === '{{session.custom.telephone}}') {
      subTypeToReturn = WidgetComponentSubType.InputTextPhone;
    } else {
      // Get component by custom merge var
      // Return component (or empty if we couldn't find any and no specified sub-type matched above)
      return this.getInputComponentByCustomMergeVar(mergeVar, inputComponents);
    }

    // Get component by sub type
    const componentIndex = inputComponents.findIndex((component) => component.subType === subTypeToReturn);
    if (componentIndex !== -1) {
      return inputComponents[componentIndex];
    } else {
      return undefined;
    }
  }

  private static getInputComponentByCustomMergeVar(
    mergeVar: string,
    inputComponents: Array<WidgetComponent>,
  ): WidgetComponent {
    const isCustom = mergeVar.indexOf(this.customInputTextFieldMergeVarPrefix) === 0;
    if (isCustom) {
      const fieldNameFromMergeVar = mergeVar.replace(this.customInputTextFieldMergeVarPrefix, '').replace('}}', '');
      return inputComponents.find((c) => c.name === fieldNameFromMergeVar);
    }
    return undefined;
  }

  public static getCustomInputTextFieldMergeVar(inputField?: WidgetComponent): string {
    return `${this.customInputTextFieldMergeVarPrefix}${inputField?.name}}}`;
  }

  private static shouldFieldTypeBeMapped(widgetComponent: WidgetComponent) {
    switch (widgetComponent.subType) {
      case WidgetComponentSubType.InputTextPassword:
        return false;
      default:
        return true;
    }
  }

  public static getDefaultNewSubscriberTrigger(): TriggerCondition {
    return {
      operator: 'Equals',
      type: 'Tags',
      argument: 'NewSubscriber',
    } as TriggerCondition;
  }

  /** Goal for a component's action. Keyed on the state too — segment count is what separates it from a state action. */
  public static getComponentActionGoalName(widgetId: number, stateId: number, componentId: number): string {
    return `oc_action_${widgetId}_${stateId}_${componentId}`;
  }

  /** Goal for a state's own action; the shorter form of {@link getComponentActionGoalName}. */
  public static getStateActionGoalName(widgetId: number, stateId: number): string {
    return `oc_action_${widgetId}_${stateId}`;
  }

  // The automation contract has its own source-type enum, so widget types are mapped over rather than passed through.
  // A state's own type is Regular/Teaser/Success, which says nothing about it being the click source, so
  // callers that link a whole state pass isWidgetState instead of relying on the type.
  public static toAutomationSourceType(
    componentType: WidgetComponentType,
    isWidgetState = false,
  ): AutomationSourceType | undefined {
    if (isWidgetState) {
      return AutomationSourceType.State;
    }
    switch (componentType) {
      case WidgetComponentType.Button:
        return AutomationSourceType.Button;
      case WidgetComponentType.ButtonList:
        return AutomationSourceType.ButtonList;
      case WidgetComponentType.Image:
        return AutomationSourceType.Image;
      case WidgetComponentType.Text:
        return AutomationSourceType.Text;
      case WidgetComponentType.StepNavigation:
        return AutomationSourceType.StepNavigation;
      default:
        return undefined;
    }
  }

  /**
   * A coupon automation is triggered by the coupon event itself rather than by a click goal, so it carries
   * the scope in typed fields. The component keeps its id only as a reference back to it.
   */
  public static generateBlankCouponAutomation(
    subType: TriggerSubType,
    widgetId: number,
    componentId: number,
    name: string,
    sourceType?: AutomationSourceType,
    sourceLabel?: string,
  ): Automation {
    const automation = new Automation();
    automation.name = name;
    automation.sourceType = sourceType;
    automation.sourceLabel = sourceLabel;
    automation.triggers = [
      {
        operator: 'Equals',
        type: TriggerType.Event,
        typeArgument: TriggerTypeArgument.Event,
        subType,
        widgetId,
        componentId,
        argument: null,
      } as unknown as TriggerCondition,
    ];
    automation.actions = [];
    return automation;
  }

  public static generateBlankComponentActionAutomation(
    goalName: string,
    name: string,
    sourceType?: AutomationSourceType,
    sourceLabel?: string,
  ): Automation {
    const automation = new Automation();
    automation.name = name;
    automation.sourceType = sourceType;
    automation.sourceLabel = sourceLabel;
    automation.triggers = [
      {
        operator: 'Equals',
        type: 'Goals',
        argument: goalName,
      } as TriggerCondition,
    ];
    automation.actions = [];
    return automation;
  }

  // A state carries no text of its own, so it is named the way the left sidebar names it (pickTitleForState:
  // the first labelled component, falling back to "State {order}") — the automation then reads as the step
  // the user sees in the editor.
  public static getComponentSourceLabel(
    component: WidgetComponent | WidgetState,
    languageService?: LanguageService,
  ): string | undefined {
    if (languageService && WidgetComponentHelpers.isWidgetState(component)) {
      return this.trimSourceLabel(WidgetComponentHelpers.pickTitleForState(component, languageService));
    }
    // Label first: components that have one (button lists, input fields) show it to the visitor, while their
    // name is an auto-generated field key and their textProperties are empty.
    const rawLabel =
      (component as WidgetComponent)?.label?.text ||
      (component as WidgetComponent)?.name ||
      (component as WidgetComponent)?.textProperties?.text ||
      this.getImageFileName(component as WidgetComponent);
    return this.trimSourceLabel(rawLabel);
  }

  private static trimSourceLabel(rawLabel: string | undefined): string | undefined {
    if (!rawLabel) {
      return undefined;
    }
    const label = rawLabel
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!label) {
      return undefined;
    }
    return label.length > 40 ? label.substring(0, 40).trim() : label;
  }

  // Images carry no text, so the uploaded file name is the only human-recognizable label.
  private static getImageFileName(component: WidgetComponent): string | undefined {
    const url = component?.url;
    if (component?.type !== WidgetComponentType.Image || !url || url.startsWith('data:')) {
      return undefined;
    }
    const fileName = url.split(/[?#]/)[0].split('/').pop();
    if (!fileName) {
      return undefined;
    }
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  }

  public static generateBlankWidgetAutomation(account: Account, automation, widgetId: number): Automation {
    automation.triggers = [];

    const widgetAutomationTrigger: any = {};
    widgetAutomationTrigger.operator = 'Equals';
    widgetAutomationTrigger.type = 'WidgetFormSubmission';
    widgetAutomationTrigger.argument = '' + widgetId;

    const emailConditionTrigger: any = {};
    emailConditionTrigger.operator = 'IsNotEmpty';
    emailConditionTrigger.type = AutomationHelpers.getDefaultConditionType(account);
    emailConditionTrigger.argument = '';
    automation.triggers.push(widgetAutomationTrigger, emailConditionTrigger);
    return automation;
  }

  static removeAllActionsFromAutomationForApp(
    existingAutomation: Automation,
    automationConfig: AutomationEditorConfig,
  ): Automation {
    if (existingAutomation && existingAutomation.actions && automationConfig) {
      const actionsOnAutomation = automationConfig.actionsConfig.flatMap((actions) =>
        actions.actionsGroup.map((action) => action.action.$type),
      );
      existingAutomation.actions = existingAutomation.actions.filter((action) => {
        if (automationConfig.type === 'step') {
          return (
            !actionsOnAutomation.includes(action.$type) ||
            (ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME) &&
              !ActionWithRealTimeScl.includes(action.$type as ACTIONS_TYPES))
          );
        } else {
          let nameAutomation = automationConfig.name;
          if (automationConfig.name === 'MailChimp') nameAutomation = 'Mailchimp';
          return (
            !action.$type.includes(nameAutomation) ||
            (ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME) &&
              !ActionWithRealTimeScl.includes(action.$type as ACTIONS_TYPES))
          );
        }
      });
    }
    return existingAutomation;
  }

  public static changeNoneToNullPreferencesProperties(data) {
    const preferencesAction = data.find((obj) => obj.$type === ACTIONS_TYPES.VoyadoUpdateConsentPreferences);
    if (preferencesAction) {
      preferencesAction.properties.forEach((property) => {
        if (preferencesAction[property.name] === 'none') {
          preferencesAction[property.name] = null;
        }
      });
    }
  }

  public static changeNoneToNullRuleUpdateTagProperties(data): any {
    if (!data.length) return data;
    data.forEach((action) => {
      if (action.tagIds === 'none' && action.$type === ACTIONS_TYPES.RuleMailerUpdateMergeTag) {
        action.tagIds = null;
      }
    });
    return data;
  }

  public static changeNullToNoneRuleUpdateTagProperties(data): any {
    if (!data.length) return data;
    data.forEach((action) => {
      if (action.tagIds === null && action.$type === ACTIONS_TYPES.RuleMailerUpdateMergeTag) {
        action.tagIds = 'none';
      }
    });
    return data;
  }

  public static changeEmptyStringToNullBrevoListIds(data): any {
    if (!data.length) return data;
    data.forEach((action) => {
      if (
        (action.listIds === '' && action.$type === ACTIONS_TYPES.BrevoCreateContact) ||
        action.$type === ACTIONS_TYPES.BrevoUpdateContact
      ) {
        action.listIds = null;
      }
      if (action.unlinkListIds === '' && action.$type === ACTIONS_TYPES.BrevoUpdateContact) {
        action.unlinkListIds = null;
      }
    });
    return data;
  }

  public static translateAbandonCartName(automation, translateService): any {
    if (
      (automation.name === 'Visitor abandons cart' || automation.name === 'Besökare överger varukorgen') &&
      automation.triggers.some((trigger) => trigger.type === 'Goals')
    ) {
      return translateService.instant('automations.abandonsCart');
    }
  }

  public static checkVoyadoRealTimeProp(
    fieldValue,
    fieldName,
    input,
    automationConfig,
    automationApiResponse?,
  ): boolean {
    if (
      automationConfig.name === IntegrationType.Voyado &&
      automationApiResponse &&
      automationApiResponse.actions?.some(
        (action) => action.$type === ACTIONS_TYPES.VoyadoAddEmailToList && action.isRunningInRealTime,
      )
    ) {
      if (
        (this.getSocialSecurityInput(input) &&
          fieldValue?.includes('{{session.custom.') &&
          (fieldName === 'socialSecurityNumber' || !fieldName)) ||
        fieldValue?.includes('{{session.custom.telephone}}')
      ) {
        return false;
      }
      return true;
    }
    return true;
  }

  public static getSocialSecurityInput(input) {
    if (
      input.validationProperties &&
      input.subType === WidgetComponentSubType.InputTextRegular &&
      [
        ValidationType.FinnishIdNumber,
        ValidationType.SwedishIdNumber,
        ValidationType.SwedishIdNumberTenDigits,
      ].includes(input.validationProperties?.type)
    ) {
      return true;
    }
  }

  public static addExtraTagForRunningInRealTime(action, prop, widgetStates) {
    if (
      action.$type === ACTIONS_TYPES.RuleMailerAddTagToSubscriber &&
      widgetStates.some(
        (state) => WidgetComponentHelpers.hasWidgetStateEmailInput(state) && this.hasSCLComponent(widgetStates),
      )
    ) {
      prop.addExtraTagForRunningInRealTime = true;
      return true;
    }
  }

  public static hasSCLComponent(widgetStates): boolean {
    const widgetState = widgetStates.states ? widgetStates : { states: widgetStates };
    const subscriptionCheckboxComponents = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(widgetState as Widget),
      [WidgetComponentSubType.ButtonListSubscriptionCheckbox],
    );
    return subscriptionCheckboxComponents?.length > 0;
  }

  public static forceUseRunningInRealTimeForVoyado(currentAppConfigurationInfo, account, isSaved?) {
    if (account.identifier !== AccountIdentifierConstants.VoyadoContactId) {
      return;
    }
    if (currentAppConfigurationInfo.name !== IntegrationType.Voyado) {
      return;
    }
    const addContactAction = currentAppConfigurationInfo.actionsConfig.find(
      (config) => config.nameGroup === TypeStep.AddContact,
    );
    addContactAction?.actionsGroup.forEach((group) => {
      if (group.action.$type !== ACTIONS_TYPES.VoyadoAddEmailToList) {
        return;
      }
      const propIsRealTime = group.action.properties?.find((prop) => prop.name === 'isRunningInRealTime');
      if (!propIsRealTime) {
        return;
      }
      // Despite the name, real time is locked only once the user has turned it on.
      if (propIsRealTime.value !== true && group.action.isRunningInRealTime !== true) {
        return;
      }
      if (isSaved) {
        group.action.isRunningInRealTime = true;
        propIsRealTime.value = true;
      }
      propIsRealTime.disabled = true;
      propIsRealTime.tooltip = 'automations.disable-turn-off-real-time-voyado-when-voyadoContactId';
    });
  }
  public static backwardCompatibilityTagIdProp(input: Automation): Automation;
  public static backwardCompatibilityTagIdProp(input: Observable<Automation>): Observable<Automation>;

  public static backwardCompatibilityTagIdProp(
    input: Automation | Observable<Automation>,
  ): Automation | Observable<Automation> {
    const transform = (automation: Automation): Automation => {
      if (automation?.actions?.length) {
        let tagIds;
        automation.actions.forEach((action: CustomModelAction) => {
          if (
            action.$type === ACTIONS_TYPES.RuleMailerAddTagToSubscriber ||
            action.$type === ACTIONS_TYPES.RuleMailerUpdateMergeTag ||
            action.$type === ACTIONS_TYPES.RuleUpdateSubscriberTags
          ) {
            if (Array.isArray(action.tagIds)) {
              tagIds = action.tagIds;
            }
            if (action.tagId && !Array.isArray(action.tagId)) {
              action.tagIds = [action.tagId];
              delete action.tagId;
            } else if (!action.tagIds && !action.tagId) {
              action.tagIds = tagIds;
            }
          }
        });
      }
      return automation;
    };
    // A failed request yields no automation at all; `in` would throw before the callers' own fallbacks run.
    if (!input) {
      return input;
    }
    if ('pipe' in input) {
      return input.pipe(map(transform));
    }
    return transform(input);
  }

  public static setNameOnBlankAutomation(campaignName, currentAccount, languageService, widget) {
    const automation = WidgetAutomationHelpers.generateBlankWidgetAutomation(currentAccount, {}, widget.id);
    if (campaignName === null) {
      automation.name = languageService.translateInstantly(
        'campaign.widget.sidebar.form-settings.gen-automation-unnamed-name',
      );
    } else {
      automation.name = languageService.translateInstantly(
        'campaign.widget.sidebar.form-settings.gen-automation-name',
        { campaignName: campaignName },
      );
    }
    return automation;
  }

  public static replaceFieldMapKey(prop, languageService): void {
    switch (prop.mapName) {
      case languageService.translateInstantly('campaign.widget.component.input-text-name.first-name-title'):
        prop.mapName = 'firstname';
        break;
      case languageService.translateInstantly('campaign.widget.component.input-text-name.last-name-title'):
        prop.mapName = 'lastname';
        break;
      case languageService.translateInstantly('campaign.widget.component.input-text-name.title'):
        prop.mapName = 'name';
        break;
    }
  }
}
